/**
 * 纯设计器组件：仅负责编辑 UI、文档状态与导出，不调用业务 API。
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragCancelEvent,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { CanvasProvider, useCanvas } from './designer/shell/CanvasContext';
import { CanvasAssetProvider } from './designer/shell/CanvasAssetContext';
import { ElementPanel } from './designer/sider/element-insert';
import { FileSettingsToolbar, TopRightToolbar } from './designer/toolbars';
import {
  Workspace,
  CanvasPointerModeSwitcher,
  CanvasDesignerOverlays,
  CanvasWorkspaceChromeProvider,
  CanvasModeProvider,
  useCanvasPointerMode,
  useWorkspaceChrome,
} from './designer/shell';
import { ExportOptionsDialog } from './designer/export/ExportOptionsDialog';
import type { ExportFormat } from './designer/export/ExportOptionsDialog';
import { renderCanvasToDataURL } from './rendering/export';
import { createPageWithSize } from './page/createPage';
import { toCanvasData } from './document/serializeDocument';
import { useCanvasStore } from './store/canvas.store';
import {
  mediaImageWorldBoundsAtPointer,
  resolveMediaImageDrop,
} from './workspace/model/mediaImageDrop';
import type { CanvasDesignerProps, CanvasDesignerApi } from './designer/shell/types';
import type { CanvasData } from './document/publicTypes';

import './shared/styles/fonts.css';
import { ensureBuiltinElementsRegistered } from './elements/registry/registerBuiltinElements';

ensureBuiltinElementsRegistered();

const SLIDE_MODE_PAGE_SIZE = { width: 1920, height: 1080 } as const;

function CanvasDesignerInner({
  mode,
  initialDocument,
  initialPageSize,
  workspacePageLayoutMode: workspacePageLayoutModeProp,
  readonly = false,
  autoEnterFullscreen = false,
  playConfig = null,
  fileName = '',
  onReady,
  onChange,
  onRequestSave,
  onRequestClose,
  onRenameFile,
  onExportError,
  onExportSuccess,
  assetAdapters,
  slots,
}: CanvasDesignerProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [workspaceSessionKey, setWorkspaceSessionKey] = useState('pending');
  const [fitCurrentPageOnMount, setFitCurrentPageOnMount] = useState(true);
  const designerContainerRef = useRef<HTMLDivElement>(null);
  const documentLoadedKeyRef = useRef<string | null>(null);

  const {
    getCurrentPage,
    pages,
    currentPageId,
    workspaceElements,
    loadState,
    fitToScreen,
    addImageElementAt,
    stageHostDomRef,
    zoom,
    panX,
    panY,
  } = useCanvas();
  const setMediaImageDropTargetPageId = useCanvasStore((s) => s.setMediaImageDropTargetPageId);

  const fitToScreenRef = useRef(fitToScreen);
  fitToScreenRef.current = fitToScreen;

  const buildDocument = useCallback((): CanvasData => {
    const st = useCanvasStore.getState();
    return toCanvasData(
      st.pages,
      st.workspaceElements,
      1,
      { zoom: st.zoom, panX: st.panX, panY: st.panY },
      st.workspacePageLayoutMode,
    );
  }, []);

  const apiRef = useRef<CanvasDesignerApi | null>(null);
  apiRef.current = {
    getDocument: buildDocument,
    loadDocument: (doc) => {
      loadState(
        doc.pages,
        doc.workspaceElements,
        doc.viewport,
        doc.workspacePageLayoutMode,
      );
    },
    resetDocument: (doc) => {
      if (!doc) {
        loadState([], []);
        return;
      }
      loadState(
        doc.pages,
        doc.workspaceElements,
        doc.viewport,
        doc.workspacePageLayoutMode,
      );
    },
    exportCurrentPage: async (options) => {
      const page = getCurrentPage();
      if (!page) throw new Error('页面未就绪');
      return renderCanvasToDataURL(page, {
        scale: options?.scale ?? 2,
        format: options?.format ?? 'png',
        quality: options?.quality,
        getImageProxyUrl: async (src) => {
          try {
            const res = await fetch(`/api/assets/proxy-image?url=${encodeURIComponent(src)}`, {
              credentials: 'include',
            });
            if (!res.ok) return null;
            const blob = await res.blob();
            return URL.createObjectURL(blob);
          } catch {
            return null;
          }
        },
      });
    },
    fitToScreen: () => {
      const workspaceEl = document.querySelector('.workspace') as HTMLElement | null;
      if (workspaceEl) fitToScreenRef.current(workspaceEl.offsetWidth, workspaceEl.offsetHeight);
    },
  };

  useEffect(() => {
    onReady?.(apiRef.current!);
  }, [onReady]);

  const resolvedInitialPageSize = mode === 'slide' ? SLIDE_MODE_PAGE_SIZE : initialPageSize;

  useLayoutEffect(() => {
    const hasPages = (initialDocument?.pages?.length ?? 0) > 0;
    const docKey = initialDocument
      ? `pages:${initialDocument.pages.length}:${initialDocument.pages[0]?.id ?? ''}:${initialDocument.workspacePageLayoutMode ?? ''}`
      : resolvedInitialPageSize
        ? `size:${resolvedInitialPageSize.width}x${resolvedInitialPageSize.height}`
        : `empty:${mode}`;
    if (documentLoadedKeyRef.current === docKey) {
      return;
    }
    documentLoadedKeyRef.current = docKey;
    setWorkspaceSessionKey(docKey);
    /** 有页时进入后由 fit 适应当前激活页（第一页），不先套用 JSON 中的 viewport */
    setFitCurrentPageOnMount(hasPages || !!resolvedInitialPageSize);

    const resolvedLayoutMode =
      workspacePageLayoutModeProp ?? initialDocument?.workspacePageLayoutMode;
    if (hasPages && initialDocument) {
      loadState(
        initialDocument.pages,
        initialDocument.workspaceElements ?? [],
        undefined,
        resolvedLayoutMode,
      );
    } else if (resolvedInitialPageSize) {
      const page = createPageWithSize(resolvedInitialPageSize.width, resolvedInitialPageSize.height);
      loadState([page], [], undefined, resolvedLayoutMode);
    } else {
      loadState([], [], undefined, resolvedLayoutMode);
    }
    setBootstrapped(true);
  }, [mode, initialDocument, resolvedInitialPageSize, workspacePageLayoutModeProp, loadState]);

  useEffect(() => {
    if (!onChange) return;
    const unsub = useCanvasStore.subscribe((state, prev) => {
      if (
        state.pages === prev.pages &&
        state.workspaceElements === prev.workspaceElements &&
        state.workspacePageLayoutMode === prev.workspacePageLayoutMode
      ) {
        return;
      }
      onChange(
        toCanvasData(
          state.pages,
          state.workspaceElements,
          1,
          { zoom: state.zoom, panX: state.panX, panY: state.panY },
          state.workspacePageLayoutMode,
        ),
        { reason: 'edit' },
      );
    });
    return unsub;
  }, [onChange]);

  useEffect(() => {
    if (!onRequestSave) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        const doc = buildDocument();
        void onRequestSave(doc);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onRequestSave, buildDocument]);

  const closePropertyPanelRef = useRef<(() => void) | null>(null);
  const closeImageSheetRef = useRef<(() => void) | null>(null);

  const lastPointerRef = useRef({ x: 0, y: 0 });
  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('pointermove', onPointerMove);
    return () => window.removeEventListener('pointermove', onPointerMove);
  }, []);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const viewportPanDragEndRef = useRef<() => void>(() => {});
  const workspaceChrome = useWorkspaceChrome();

  const {
    handPanToolActive,
    isHandUiActive,
    selectPointerMode,
    selectHandMode,
    isSpacePressed,
  } = useCanvasPointerMode({
    onSpaceRelease: () => viewportPanDragEndRef.current(),
    spacePanEnabled: !(workspaceChrome?.isFullscreen ?? false) && !readonly,
  });

  const [activeDragData, setActiveDragData] = useState<{ url: string; name?: string } | null>(null);

  const syncMediaImageDropTarget = useCallback(() => {
    const host = stageHostDomRef.current;
    if (!host) {
      setMediaImageDropTargetPageId(null);
      return;
    }
    const bounds = mediaImageWorldBoundsAtPointer(
      lastPointerRef.current.x,
      lastPointerRef.current.y,
      host.getBoundingClientRect(),
      panX,
      panY,
      zoom,
    );
    const resolved = resolveMediaImageDrop(pages, bounds);
    setMediaImageDropTargetPageId(resolved.highlightPageId);
  }, [pages, panX, panY, zoom, stageHostDomRef, setMediaImageDropTargetPageId]);

  const clearMediaImageDropTarget = useCallback(() => {
    setMediaImageDropTargetPageId(null);
  }, [setMediaImageDropTargetPageId]);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const data = event.active.data.current;
      if (data && (data as { type?: string }).type === 'media-image') {
        const { url, name } = data as { url: string; name?: string };
        if (url) {
          setActiveDragData({ url, name });
          syncMediaImageDropTarget();
        }
      }
    },
    [syncMediaImageDropTarget],
  );

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      const data = event.active.data.current;
      if (!data || (data as { type?: string }).type !== 'media-image') return;
      syncMediaImageDropTarget();
    },
    [syncMediaImageDropTarget],
  );

  const handleDragCancel = useCallback(
    (_event: DragCancelEvent) => {
      setActiveDragData(null);
      clearMediaImageDropTarget();
    },
    [clearMediaImageDropTarget],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDragData(null);
      clearMediaImageDropTarget();
      const { active, over } = event;
      if (over?.id !== 'workspace-drop') return;
      const data = active.data.current;
      if (!data || (data as { type?: string }).type !== 'media-image') return;
      const { url, name } = data as { url: string; name?: string };
      if (!url) return;
      const host = stageHostDomRef.current;
      if (!host) return;
      const bounds = mediaImageWorldBoundsAtPointer(
        lastPointerRef.current.x,
        lastPointerRef.current.y,
        host.getBoundingClientRect(),
        panX,
        panY,
        zoom,
      );
      const drop = resolveMediaImageDrop(pages, bounds);
      addImageElementAt({
        src: url,
        x: drop.x,
        y: drop.y,
        name: name ?? undefined,
        via: drop.via,
        pageId: drop.pageId,
      });
    },
    [
      pages,
      panX,
      panY,
      zoom,
      addImageElementAt,
      stageHostDomRef,
      clearMediaImageDropTarget,
    ],
  );

  const getExportDownloadName = useCallback(
    (ext: string): string => {
      const rawName = fileName.trim() || '未命名文件';
      const safeName = rawName.replace(/[/\\:*?"<>|]/g, '_').replace(/\s+/g, ' ').trim() || '未命名文件';
      if (pages.length <= 1) return `${safeName}.${ext}`;
      const pageIndex = pages.findIndex((c) => c.id === currentPageId);
      const oneBased = pageIndex >= 0 ? pageIndex + 1 : 1;
      return `${safeName}-${oneBased}.${ext}`;
    },
    [fileName, pages, currentPageId],
  );

  const handleExport = useCallback(
    async (format: 'png' | 'jpeg' = 'png', options?: { scale?: number; quality?: number }) => {
      if (isDownloading) return;
      const currentPage = getCurrentPage();
      if (!currentPage) {
        onExportError?.('请先选择一个页面');
        return;
      }
      setIsDownloading(true);
      const scale = options?.scale ?? 2;
      const quality = options?.quality ?? (format === 'jpeg' ? 0.92 : undefined);
      try {
        if (typeof document !== 'undefined' && document.fonts?.ready) {
          await document.fonts.ready;
        }
        const dataUrl = await renderCanvasToDataURL(currentPage, {
          scale,
          format,
          quality,
          getImageProxyUrl: async (src) => {
            try {
              const res = await fetch(`/api/assets/proxy-image?url=${encodeURIComponent(src)}`, {
                credentials: 'include',
              });
              if (!res.ok) return null;
              const blob = await res.blob();
              return URL.createObjectURL(blob);
            } catch {
              return null;
            }
          },
        });
        const ext = format === 'jpeg' ? 'jpg' : 'png';
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = getExportDownloadName(ext);
        a.click();
        onExportSuccess?.(format);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        onExportError?.(msg ? `导出失败：${msg}` : '导出失败');
      } finally {
        setIsDownloading(false);
      }
    },
    [isDownloading, getCurrentPage, getExportDownloadName, onExportError, onExportSuccess],
  );

  const handleExportWithOptions = useCallback(
    (format: ExportFormat, options: { scale: number; quality?: number }) => {
      void handleExport(format, options);
    },
    [handleExport],
  );

  if (!bootstrapped && (initialDocument || resolvedInitialPageSize)) {
    return (
      <div className="designer-container fixed inset-0 z-40 bg-surface" aria-hidden />
    );
  }

  return (
    <CanvasWorkspaceChromeProvider>
      <div
        ref={designerContainerRef}
        data-canvas-mode={mode}
        className="designer-container workspace fixed inset-0 z-40 min-h-0 min-w-0 overflow-hidden bg-background"
      >
        <ExportOptionsDialog
          open={exportDialogOpen}
          onOpenChange={setExportDialogOpen}
          onExport={handleExportWithOptions}
          isExporting={isDownloading}
        />

        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragCancel={handleDragCancel}
          onDragEnd={handleDragEnd}
        >
          <Workspace
            key={workspaceSessionKey}
            className="absolute inset-0 z-0"
            readonly={readonly}
            autoEnterFullscreen={autoEnterFullscreen}
            playConfig={playConfig ?? undefined}
            onExitComponent={onRequestClose}
            isSpacePressed={isSpacePressed}
            handPanToolActive={handPanToolActive}
            viewportPanDragEndRef={viewportPanDragEndRef}
            fitCurrentPageOnMount={fitCurrentPageOnMount}
          />

          <CanvasDesignerOverlays />

          <FileSettingsToolbar
            currentFileName={fileName}
            onRenameFile={onRenameFile}
          />

          {!readonly ? (
            <ElementPanel
              overlayContainerRef={designerContainerRef}
              imagePanel={slots?.imagePanel}
              onDismissPropertyPanel={() => closePropertyPanelRef.current?.()}
              onRegisterCloseImageSheet={(close) => {
                closeImageSheetRef.current = close;
              }}
            />
          ) : null}

          <TopRightToolbar
            onExportPng={() => handleExport('png')}
            onExportJpeg={() => handleExport('jpeg')}
            isExporting={isDownloading}
          />

          {!readonly ? (
            <CanvasPointerModeSwitcher
              isHandActive={isHandUiActive}
              onSelectPointer={selectPointerMode}
              onSelectHand={selectHandMode}
              onRegisterClosePropertyPanel={(close) => {
                closePropertyPanelRef.current = close;
              }}
              onPropertyPanelOpenChange={(open) => {
                if (open) closeImageSheetRef.current?.();
              }}
            />
          ) : null}

          <DragOverlay dropAnimation={null} zIndex={999}>
            {activeDragData ? (
              <div
                className="rounded-lg border-2 border-primary/60 bg-surface shadow-lg overflow-hidden pointer-events-none flex flex-col"
                style={{ width: 120, height: 120 }}
              >
                <div className="flex-1 min-h-0 bg-default-100/40">
                  <img
                    src={activeDragData.url}
                    alt={activeDragData.name ?? ''}
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                </div>
                {activeDragData.name ? (
                  <div className="px-2 py-1 text-[10px] truncate text-foreground bg-surface border-t border-divider">
                    {activeDragData.name}
                  </div>
                ) : null}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </CanvasWorkspaceChromeProvider>
  );
}

export function CanvasDesigner(props: CanvasDesignerProps) {
  const { assetAdapters, mode, ...rest } = props;
  return (
    <CanvasProvider>
      <CanvasModeProvider mode={mode}>
        <CanvasAssetProvider adapters={assetAdapters}>
          <CanvasDesignerInner {...rest} mode={mode} assetAdapters={assetAdapters} />
        </CanvasAssetProvider>
      </CanvasModeProvider>
    </CanvasProvider>
  );
}
